import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import * as utilApi from 'api/util';
import { getJawByToothNumber } from 'lib/clinic/library';
import { all, takeLatest } from 'redux-saga/effects';
import store from 'store';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { createFetchAction, createSaga, fetchInitialState, fetchReducerActions } from 'store/utils';

export const fetch_teeth_indication_format = createFetchAction('fetch_teeth_indication_format');
export const fetch_teeth_indication_info = createFetchAction('fetch_teeth_indication_info');
export const fetch_teeth_indication_option = createFetchAction('fetch_teeth_indication_option');
export const fetch_teeth_indication_option_offline = createFetchAction(
  'fetch_teeth_indication_option_offline',
);
export const fetch_teeth_implant_library = createFetchAction('fetch_teeth_implant_library');
export const fetch_advertisement = createFetchAction('fetch_advertisement');
export const fetch_currency_list = createFetchAction('fetch_currency_list');
export const fetch_country_list = createFetchAction('fetch_country_list');
export const fetch_country_region_list = createFetchAction('fetch_country_region_list');
export const fetch_remake_type_list = createFetchAction('fetch_remake_type_list');
export const fetch_admin_notification = createFetchAction('fetch_admin_notification');
export const fetch_support_language_list = createFetchAction('fetch_support_language_list');
export const fetch_admin_translation = createFetchAction('fetch_admin_translation');
export const audio_transcription = createFetchAction('audio_transcription');
export const fetch_legal_agreement_list = createFetchAction('fetch_legal_agreement_list');
export const fetch_legal_data = createFetchAction('fetch_legal_data');
export const fetch_legal_category_list = createFetchAction('fetch_legal_category_list');
export const fetch_user_type_list = createFetchAction('fetch_user_type_list');

interface InitialState {
  teeth: object[];

  indicationFormat: object | any;
  indicationInfo: object | any;
  indicationOption: object | any;
  indicationOptionOffline: object | any;
  implantLibrary: object | any;
  advertisement: object | any;
  currencyList: object | any;
  countryList: object | any;
  regionList: object | any;
  remakeList: object | any;
  adminNotification: object | any;
  supportLanguageList: object | any;
  adminTranslation: object | any;
  audioTranscription: object | any;
  agreementList: object | any;
  legalData: object | any;
  legalCategoryList: object | any;
  usertypeList: object | any;
}

const { ipcRenderer } = window;

const initialState: InitialState = {
  teeth: [
    {
      indicationIdx: 11,
      preparationType: 3,
      number: 22,
      reconstructionType: 31,
      material: 5,
      color: '#335C15',
      implantType: 4,
      situScan: 1,
      separateGingivaScan: 0,
      teethTreatmentIdx: 852,
    },
    {
      indicationIdx: 11,
      preparationType: 3,
      number: 23,
      reconstructionType: 31,
      material: 5,
      color: '#335C15',
      implantType: 4,
      situScan: 1,
      separateGingivaScan: 0,
      teethTreatmentIdx: 852,
    },
  ],
  indicationFormat: { ...fetchInitialState, data: null },
  indicationInfo: { ...fetchInitialState, data: null },
  indicationOption: { ...fetchInitialState, data: null },
  indicationOptionOffline: { ...fetchInitialState, data: null },
  implantLibrary: { ...fetchInitialState, data: null },
  advertisement: { ...fetchInitialState, data: null },
  currencyList: { ...fetchInitialState, data: null },
  countryList: { ...fetchInitialState, data: null },
  regionList: { ...fetchInitialState, data: null },
  remakeList: { ...fetchInitialState, data: null },
  adminNotification: { ...fetchInitialState, data: null },
  supportLanguageList: { ...fetchInitialState, data: null },
  adminTranslation: { ...fetchInitialState, data: null },
  audioTranscription: { ...fetchInitialState, data: null },
  agreementList: { ...fetchInitialState, data: null },
  legalData: { ...fetchInitialState, data: null },
  legalCategoryList: { ...fetchInitialState, data: null },
  usertypeList: { ...fetchInitialState, data: null },
};

const slice = createSlice({
  name: 'util',
  initialState,
  reducers: {
    set_teeth: (state, { payload }: PayloadAction<object[]>) => {
      state.teeth = payload;
    },
    ...fetchReducerActions(fetch_teeth_indication_format, 'indicationFormat'),
    ...fetchReducerActions(fetch_teeth_indication_info, 'indicationInfo'),
    ...fetchReducerActions(fetch_teeth_indication_option, 'indicationOption', {
      success: (state: any, data: any) => {
        const teeth = useProjectDetailStore.getState().teeth;
        const isPhotogrammetry = useProjectDetailStore.getState().isPhotogrammetry;

        if (!teeth || teeth?.length === 0 || !isPhotogrammetry) return;

        const newOption = {
          ...data.payload,
          default: {
            ...data.payload.default,
            situScanUpper: {
              ...data.payload.default.situScan,
            },
            situScanLower: {
              ...data.payload.default.situScan,
            },
          },
        };

        state.indicationOption.data = newOption;
      },
    }),
    ...fetchReducerActions(fetch_teeth_indication_option_offline, 'indicationOptionOffline'),
    ...fetchReducerActions(fetch_teeth_implant_library, 'implantLibrary'),
    ...fetchReducerActions(fetch_advertisement, 'advertisement'),
    ...fetchReducerActions(fetch_currency_list, 'currencyList'),
    ...fetchReducerActions(fetch_country_list, 'countryList'),
    ...fetchReducerActions(fetch_country_region_list, 'regionList'),
    ...fetchReducerActions(fetch_remake_type_list, 'remakeList'),
    ...fetchReducerActions(fetch_admin_notification, 'adminNotification'),
    ...fetchReducerActions(fetch_support_language_list, 'supportLanguageList'),
    ...fetchReducerActions(fetch_admin_translation, 'adminTranslation'),
    ...fetchReducerActions(audio_transcription, 'audioTranscription'),
    ...fetchReducerActions(fetch_legal_agreement_list, 'agreementList'),
    ...fetchReducerActions(fetch_legal_data, 'legalData'),
    ...fetchReducerActions(fetch_legal_category_list, 'legalCategoryList'),
    ...fetchReducerActions(fetch_user_type_list, 'usertypeList'),
  },
});

export const actions: any = slice.actions;

export function* utilSaga() {
  yield all([
    takeLatest(
      actions.fetch_teeth_indication_format_request,
      createSaga(actions, 'fetch_teeth_indication_format', utilApi.fetchTeethIndicationFormat),
    ),
    takeLatest(
      actions.fetch_teeth_indication_info_request,
      createSaga(actions, 'fetch_teeth_indication_info', utilApi.fetchTeethIndicationInfo),
    ),
    takeLatest(
      actions.fetch_teeth_indication_option_request,
      createSaga(actions, 'fetch_teeth_indication_option', utilApi.fetchTeethIndicationOption),
    ),
    takeLatest(
      actions.fetch_teeth_indication_option_offline_request,
      createSaga(
        actions,
        'fetch_teeth_indication_option_offline',
        utilApi.fetchTeethIndicationOptionOffline,
      ),
    ),
    takeLatest(
      actions.fetch_teeth_implant_library_request,
      createSaga(actions, 'fetch_teeth_implant_library', utilApi.fetchTeethImplantLibrary),
    ),
    takeLatest(
      actions.fetch_advertisement_request,
      createSaga(actions, 'fetch_advertisement', utilApi.fetchAdvertisement),
    ),
    takeLatest(
      actions.fetch_currency_list_request,
      createSaga(actions, 'fetch_currency_list', utilApi.fetchCurrencyList),
    ),
    takeLatest(
      actions.fetch_country_list_request,
      createSaga(actions, 'fetch_country_list', utilApi.fetchCountryList),
    ),
    takeLatest(
      actions.fetch_country_region_list_request,
      createSaga(actions, 'fetch_country_region_list', utilApi.fetchCountryRegionList),
    ),
    takeLatest(
      actions.fetch_legal_agreement_list_request,
      createSaga(actions, 'fetch_legal_agreement_list', utilApi.fetchLegalList),
    ),
    takeLatest(
      actions.fetch_legal_data_request,
      createSaga(actions, 'fetch_legal_data', utilApi.fetchLegalData),
    ),
    takeLatest(
      actions.fetch_legal_category_list_request,
      createSaga(actions, 'fetch_legal_category_list', utilApi.fetchLegalCategoryList),
    ),
    takeLatest(
      actions.fetch_remake_type_list_request,
      createSaga(actions, 'fetch_remake_type_list', utilApi.fetchRemakeList),
    ),
    takeLatest(
      actions.fetch_admin_notification_request,
      createSaga(actions, 'fetch_admin_notification', utilApi.fetchAdminNotification),
    ),
    takeLatest(
      actions.fetch_support_language_list_request,
      createSaga(actions, 'fetch_support_language_list', utilApi.fetchSupportLanguageList),
    ),
    takeLatest(
      actions.fetch_admin_translation_request,
      createSaga(actions, 'fetch_admin_translation', utilApi.fetchAdminTranslation),
    ),
    takeLatest(
      actions.audio_transcription_request,
      createSaga(actions, 'audio_transcription', utilApi.audioTranscription),
    ),
    takeLatest(
      actions.fetch_user_type_list_request,
      createSaga(actions, 'fetch_user_type_list', utilApi.fetchUsertypeList),
    ),
  ]);
}

export default slice.reducer;
